// L3 — survey graph and forms (CREATIVE-MODEL-V1.md §11, SURVEY-ENGINE.md §4–§5).
// The graph is DATA so these properties are PROVEN statically, never repaired:
// acyclic, every question reachable, every path ends at END, at most
// SURVEY_MAX_QUESTIONS_PER_PATH questions shown on any path.

import { SURVEY_GUIDANCE, SURVEY_MAX_QUESTIONS_PER_PATH } from "../limits.js";
import type { SurveyQuestion, TextContent } from "../schema/document.js";
import type { IssueSink } from "../issues.js";
import { subtree, type DocIndex } from "./context.js";

export const SURVEY_END = "END";

/** Distinct successors of a question (option-level branching for single choice). */
export function successors(q: SurveyQuestion): Array<string | undefined> {
  if (q.kind === "single") {
    const out = new Set<string | undefined>(q.options.map((o) => o.next ?? q.next));
    return [...out];
  }
  return [q.next];
}

interface PathStats {
  readonly length: number;
  readonly research: number;
  readonly demographic: number;
}

export function validateSurvey(index: DocIndex, sink: IssueSink): void {
  const survey = index.doc.survey;
  if (!survey) return;
  const sp = "$.survey";
  const errorsBefore = sink.errorCount;
  if (survey.researchObjective.trim() === "")
    sink.add("REQUIRED", `${sp}.researchObjective`, "A research objective is required before building questions.", {});

  // Presentation.
  const pres = survey.presentation;
  const sceneRef = (id: string, path: string) => {
    if (!index.scenes.has(id)) sink.add("UNKNOWN_REFERENCE", path, `Unknown scene "${id}".`, { kind: "scene", id });
  };
  sceneRef(pres.sceneId, `${sp}.presentation.sceneId`);
  sceneRef(pres.completionSceneId, `${sp}.presentation.completionSceneId`);
  if (pres.exitSceneId !== undefined) sceneRef(pres.exitSceneId, `${sp}.presentation.exitSceneId`);
  if (survey.consent.sceneId !== undefined) sceneRef(survey.consent.sceneId, `${sp}.consent.sceneId`);
  if (survey.consent.required && survey.consent.sceneId === undefined)
    sink.add("REQUIRED", `${sp}.consent.sceneId`, "Required consent names the scene that presents it.", {});
  const scene = index.scenes.get(pres.sceneId)?.value;
  if (scene) {
    const hasQuestion = subtree(index, scene.children).some((id) => {
      const el = index.elements.get(id)?.value;
      return el?.type === "component" && el.component === "ia.survey-question@1" && el.props.survey === survey.id;
    });
    if (!hasQuestion)
      sink.add(
        "SURVEY_PRESENTATION_INVALID",
        `${sp}.presentation.sceneId`,
        `Scene "${pres.sceneId}" must contain an ia.survey-question@1 bound to survey "${survey.id}".`,
        {}
      );
  }

  // Ids and references.
  const seen = new Set<string>();
  survey.questions.forEach((q, i) => {
    const qp = `${sp}.questions[${String(i)}]`;
    if (q.id === SURVEY_END)
      sink.add("RESERVED_ID", `${qp}.id`, `"${SURVEY_END}" is reserved for the end of the survey.`, {});
    if (seen.has(q.id)) sink.add("DUPLICATE_ID", `${qp}.id`, `Duplicate identifier "${q.id}".`, { id: q.id });
    seen.add(q.id);
    if (q.kind !== "scale") {
      const opts = new Set<string>();
      q.options.forEach((o, j) => {
        if (opts.has(o.id))
          sink.add("DUPLICATE_ID", `${qp}.options[${String(j)}].id`, `Duplicate option "${o.id}".`, { id: o.id });
        opts.add(o.id);
      });
    }
    if (q.kind === "multi" && q.maxSelections > q.options.length)
      sink.add("SURVEY_OPTIONS_INVALID", `${qp}.maxSelections`, "maxSelections exceeds the number of options.", {
        options: q.options.length,
      });
    const checkNext = (next: string | undefined, path: string) => {
      if (next === undefined)
        sink.add("SURVEY_NO_EXIT", path, `Question "${q.id}" has an answer with no next question or END.`, {
          question: q.id,
        });
      else if (next !== SURVEY_END && !index.questions.has(next))
        sink.add("UNKNOWN_REFERENCE", path, `Unknown question "${next}".`, { kind: "question", id: next });
    };
    if (q.kind === "single")
      q.options.forEach((o, j) => {
        checkNext(o.next ?? q.next, `${qp}.options[${String(j)}].next`);
      });
    else checkNext(q.next, `${qp}.next`);
  });
  if (!index.questions.has(survey.startQuestionId))
    sink.add("UNKNOWN_REFERENCE", `${sp}.startQuestionId`, `Unknown question "${survey.startQuestionId}".`, {
      kind: "question",
      id: survey.startQuestionId,
    });
  if (sink.errorCount > errorsBefore) return; // graph analysis needs sound references

  // Graph: cycles (DFS with colors), longest path, reachability.
  const color = new Map<string, "grey" | "black">();
  const stats = new Map<string, PathStats>();
  const graph = { cyclic: false };
  const visit = (id: string, stack: string[]): PathStats => {
    const q = index.questions.get(id);
    if (!q) return { length: 0, research: 0, demographic: 0 };
    const memo = stats.get(id);
    if (memo) return memo;
    if (color.get(id) === "grey") {
      if (!graph.cyclic)
        sink.add(
          "SURVEY_CYCLE",
          q.path,
          `Branching loops back: ${[...stack.slice(stack.indexOf(id)), id].join(" → ")}.`,
          {
            chain: [...stack.slice(stack.indexOf(id)), id].join("|"),
          }
        );
      graph.cyclic = true;
      return { length: 0, research: 0, demographic: 0 };
    }
    color.set(id, "grey");
    let best: PathStats = { length: 0, research: 0, demographic: 0 };
    for (const next of successors(q.value)) {
      if (next === undefined || next === SURVEY_END) continue;
      const s = visit(next, [...stack, next]);
      best = {
        length: Math.max(best.length, s.length),
        research: Math.max(best.research, s.research),
        demographic: Math.max(best.demographic, s.demographic),
      };
    }
    color.set(id, "black");
    const own: PathStats = {
      length: best.length + 1,
      research: best.research + (q.value.category === "research" ? 1 : 0),
      demographic: best.demographic + (q.value.category === "demographic" ? 1 : 0),
    };
    stats.set(id, own);
    return own;
  };
  const root = visit(survey.startQuestionId, [survey.startQuestionId]);
  if (graph.cyclic) return;
  if (root.length > SURVEY_MAX_QUESTIONS_PER_PATH)
    sink.add(
      "SURVEY_PATH_TOO_LONG",
      `${sp}.questions`,
      `A participant could see ${String(root.length)} questions; the maximum per path is ${String(SURVEY_MAX_QUESTIONS_PER_PATH)}.`,
      {
        length: root.length,
        max: SURVEY_MAX_QUESTIONS_PER_PATH,
      }
    );
  if (root.research > SURVEY_GUIDANCE.research || root.demographic > SURVEY_GUIDANCE.demographic)
    sink.warn(
      "SURVEY_STRUCTURE_GUIDANCE",
      `${sp}.questions`,
      `Recommended path structure is up to ${SURVEY_GUIDANCE.research} research + ${SURVEY_GUIDANCE.demographic} demographic questions.`,
      {
        research: root.research,
        demographic: root.demographic,
      }
    );
  for (const [id, q] of index.questions)
    if (!stats.has(id))
      sink.add("SURVEY_UNREACHABLE_QUESTION", q.path, `Question "${id}" cannot be reached from the start question.`, {
        question: id,
      });
}

const CONTACT_WORDS =
  /\b(e-?mail|correo|tel[eé]fono|phone|celular|m[oó]vil|whats ?app|direcci[oó]n|address|dni|dpi|ssn|pasaporte|passport|nombre completo|full name|apellido|surname)\b/i;
const plain = (t: TextContent) => t.runs.map((r) => ("text" in r ? r.text : "")).join(" ");

export function validateForms(index: DocIndex, sink: IssueSink): void {
  (index.doc.forms ?? []).forEach((form, fi) => {
    const fp = `$.forms[${String(fi)}]`;
    const ids = new Set<string>();
    form.fields.forEach((field, i) => {
      const p = `${fp}.fields[${String(i)}]`;
      if (ids.has(field.id)) sink.add("DUPLICATE_ID", `${p}.id`, `Duplicate field "${field.id}".`, { id: field.id });
      ids.add(field.id);
      if (field.control === "text" || field.control === "textarea") {
        const v = field.validation;
        if (v?.minLength !== undefined && v.maxLength !== undefined && v.minLength > v.maxLength)
          sink.add("INVALID_VALUE", `${p}.validation`, "minLength exceeds maxLength.", {});
        if (CONTACT_WORDS.test(plain(field.label)))
          sink.warn(
            "POSSIBLE_PII_REQUEST",
            `${p}.label`,
            "This field appears to ask for personal or contact data. Initial V1 never collects it; consider removing the field.",
            {}
          );
      } else if (field.control !== "rating") {
        const opts = new Set<string>();
        field.options.forEach((o, j) => {
          if (opts.has(o.id))
            sink.add("DUPLICATE_ID", `${p}.options[${String(j)}].id`, `Duplicate option "${o.id}".`, { id: o.id });
          opts.add(o.id);
        });
      }
    });
  });
}
